import { TeamModel } from '@/features/teams/types/TeamModel';
import { defaultGcTime, defaultStaleTime } from '@/shared/constants';
import { isInvalid } from '@/shared/services/isInvalid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { useEffect } from 'react';

export function getQueryKey(leagueId?: number, seasonId?: number) {
  // seasonId is a number — checking it against 'string' made isInvalid() always
  // return true, so every season (and ClipsForm's deliberately unscoped call)
  // collapsed onto the same ['GameTeams', leagueId] entry and overwrote each other.
  return isInvalid(seasonId, 'number') ? ['GameTeams', leagueId] : ['GameTeams', leagueId, seasonId]
}

const useGameTeamsQuery = (leagueId?: number, seasonId?: number, opt?: any) => {
  const queryClient = useQueryClient();
  const isValidLeague = !isInvalid(leagueId, 'number');
  
  // Effect to remove invalid queries
  useEffect(() => {
    if (!isValidLeague) {
      queryClient.removeQueries({ queryKey: getQueryKey(leagueId, seasonId) });
    }
  }, [leagueId, seasonId, isValidLeague, queryClient]);
  
  return useQuery({
    queryKey: getQueryKey(leagueId, seasonId),
    queryFn: async (): Promise<TeamModel[]> => {
      const url = seasonId ? `/v1.0/teams?seasonId=${seasonId}` : '/v1.0/teams';
      const { data } = await get(url);
      return data;
    },
    staleTime: defaultStaleTime,
    gcTime: defaultGcTime,
    // Run as soon as we have a usable league. This used to be a hard `false`,
    // which meant any caller that did not pass `{ enabled: true }` (GamesForm
    // and GamesFormV2 both) silently got `placeholderData: []` forever — an
    // empty team autocomplete with no error and no request in the network tab.
    enabled: isValidLeague,
    placeholderData: [],
    ...opt,
  });
};

export default useGameTeamsQuery;